import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Item registration — the shared library a store's catalogue is assigned from.
 *
 * A registration item is described once, centrally: names in both languages,
 * its barcodes, its pictures. Assigning it to a store copies that description
 * into the store's catalogue with a cost and a price. That is why the page has
 * two flows and not one — describing an item and stocking it are different
 * jobs, done by different people.
 */

export interface RegistrationItem {
  id: string;
  gtin: string | null;
  brand: string | null;
  name: string | null;
  nameSpa: string | null;
  description: string | null;
  descriptionSpa: string | null;
  searchTags: string | null;
  itemImage: string | null;

  suggestedDivision: string | null;
  suggestedGroup: string | null;
  suggestedCategoryName: string | null;

  isPackagingSensitive: boolean;
  isTemperatureSensitive: boolean;
  isAlcoholicBeverage: boolean;
  isPerishable: boolean;
  isEbt: boolean;
  idRequired: boolean;
  hasPluCode: boolean;
  hasRandomWeight: boolean;

  pack: number | null;
  taxRule: number | null;
  munTaxPct: number | null;
  stateTaxPct: number | null;

  itemThumbnail: string | null;
  isDeleted: boolean;
  createdAt: string | null;
  editedAt: string | null;
  /** Answered by the search when it is asked to check. */
  isAlreadyAssigned: boolean;
  /** The row arrives with both already attached. */
  gtins: RegistrationGtin[] | null;
  itemMedias: RegistrationMedia[] | null;
}

/** What assigning adds on top of the shared description. */
export interface AssignInput extends RegistrationItem {
  cost: number | null;
  price: number | null;
}

/**
 * The defaults a new registration item starts from.
 *
 * The tax figures are Puerto Rico's — 10.5% state, 1% municipal, both applying
 * — which is right for every client on this platform today and is where the
 * original also started.
 */
export const NEW_REGISTRATION_ITEM: Omit<RegistrationItem, 'id'> = {
  gtin: '',
  brand: '',
  name: '',
  nameSpa: '',
  description: '',
  descriptionSpa: '',
  searchTags: '',
  itemImage: '',
  suggestedDivision: '',
  suggestedGroup: '',
  suggestedCategoryName: '',
  isPackagingSensitive: false,
  isTemperatureSensitive: false,
  isAlcoholicBeverage: false,
  isPerishable: false,
  isEbt: false,
  idRequired: false,
  hasPluCode: false,
  hasRandomWeight: false,
  pack: 1,
  taxRule: 3,
  munTaxPct: 0.01,
  stateTaxPct: 0.105,
  itemThumbnail: '',
  isDeleted: false,
  createdAt: null,
  editedAt: null,
  isAlreadyAssigned: false,
  gtins: null,
  itemMedias: null,
};

export function useRegistrationSearch(model: {
  searchTerm: string;
  page: number;
  size: number;
}) {
  return useQuery({
    queryKey: ['item-registration', 'search', model],
    queryFn: () =>
      unwrap<{ items: RegistrationItem[]; total: number }>(
        // Without this the rows come back with no idea whether the store
        // already stocks them, which is the one thing the list is scanned for.
        api.post('itemregistration/search', { ...model, checkIfAssigned: true })
      ),
    placeholderData: keepPreviousData,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['item-registration'] });
}

/**
 * Create answers with the saved row, so the drawer can carry on with its id.
 * Update answers with nothing at all — hence the nullable return.
 */
export function useSaveRegistrationItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (item: Partial<RegistrationItem>) =>
      unwrap<RegistrationItem | null>(
        item.id
          ? api.put('itemregistration/items', item)
          : api.post('itemregistration/items', item)
      ),
    onSuccess: invalidate,
  });
}

/** Archive, not delete — the row leaves the list but its history stays. */
export function useArchiveRegistrationItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`itemregistration/items/${id}`)),
    onSuccess: invalidate,
  });
}

/**
 * `AssignItemToCatalogBindingModel` renames three fields on the way out: what
 * the library calls a *suggested* division, group and category is, once a store
 * takes it, simply its division, group and category. Spreading the item would
 * have sent the suggested names and dropped all three.
 */
export function useAssignItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      item,
      businessIds,
    }: {
      item: AssignInput;
      /** Empty assigns to the selected store only. */
      businessIds: string[];
    }) =>
      unwrap<unknown>(
        api.post('itemregistration/assign', {
          registrationItemId: item.id,
          businessIdsToAssign: businessIds,
          gtin: item.gtin,
          brand: item.brand,
          name: item.name,
          description: item.description,
          nameSpa: item.nameSpa,
          descriptionSpa: item.descriptionSpa,
          searchTags: item.searchTags,
          isPackagingSensitive: item.isPackagingSensitive,
          isTemperatureSensitive: item.isTemperatureSensitive,
          isAlcoholicBeverage: item.isAlcoholicBeverage,
          isPerishable: item.isPerishable,
          isEbt: item.isEbt,
          idRequired: item.idRequired,
          hasPluCode: item.hasPluCode,
          hasRandomWeight: item.hasRandomWeight,
          division: item.suggestedDivision,
          group: item.suggestedGroup,
          categoryId: item.suggestedCategoryName,
          pack: item.pack,
          taxRule: item.taxRule,
          munTaxPct: item.munTaxPct,
          stateTaxPct: item.stateTaxPct,
          cost: item.cost,
          price: item.price,
        })
      ),
    onSuccess: invalidate,
  });
}

/**
 * Push this item's pictures onto the catalogue rows already assigned from it.
 *
 * `replace` is the whole question: adding leaves what a store already has and
 * appends, replacing throws it away first.
 */
export function useApplyMedia() {
  return useMutation({
    mutationFn: ({ id, replace }: { id: string; replace: boolean }) =>
      unwrap<unknown>(
        api.post('itemregistration/media/apply', {
          registrationItemId: id,
          replaceItemMedia: replace,
        })
      ),
  });
}

// ------------------------------------------------------------------ gtins

export interface RegistrationGtin {
  id: string;
  gtin: string;
  isPrimary: boolean;
}

export function useRegistrationGtins(itemId: string | null) {
  return useQuery({
    queryKey: ['item-registration', 'gtins', itemId],
    queryFn: () =>
      unwrap<RegistrationGtin[]>(
        api.get<ApiEnvelope<RegistrationGtin[]>>(
          `itemregistration/gtins/${itemId}`
        )
      ),
    enabled: !!itemId,
  });
}

function useInvalidateGtins(itemId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['item-registration', 'gtins', itemId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['item-registration', 'search'],
    });
  };
}

export function useCreateGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtin: string) =>
      unwrap<unknown>(
        api.post('itemregistration/gtins/create', {
          registrationItemId: itemId,
          gtin: gtin.trim(),
        })
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtinId: string) =>
      unwrap<unknown>(api.delete(`itemregistration/gtins/${gtinId}`)),
    onSuccess: invalidate,
  });
}

/** Exactly one barcode is primary, and it is the one the item shows as its own. */
export function useSetPrimaryGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtinId: string) =>
      unwrap<unknown>(
        api.post(`itemregistration/gtins/${itemId}/${gtinId}/primary`, {})
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------ media

/** `MediaType` — one list holds both, told apart by this. */
export const MEDIA_TYPE = { picture: 0, video: 1 } as const;

/** `RegistrationItemMediaDto`. */
export interface RegistrationMedia {
  id: string;
  registrationItemId?: string | null;
  url: string | null;
  /** Required on a video: it is the frame shown before it plays. */
  thumbnailUrl: string | null;
  type: number;
  ordering: number | null;
}

/**
 * The item's media, as one list.
 *
 * It comes back under `data.results` — a flat array with a `type` on each row —
 * not as separate picture and video collections. Reading it as two named
 * collections is why the drawer showed nothing for items that plainly had
 * images.
 */
export function useRegistrationMedia(itemId: string | null) {
  return useQuery({
    queryKey: ['item-registration', 'media', itemId],
    queryFn: () =>
      unwrap<{ results: RegistrationMedia[] }>(
        api.get<ApiEnvelope<{ results: RegistrationMedia[] }>>(
          `itemregistration/catalog/media/${itemId}`
        )
      ),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)
      ),
    enabled: !!itemId,
  });
}

function useInvalidateMedia(itemId: string | null) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['item-registration', 'media', itemId],
    });
}

/** `RegistrationItemMediaBindingModel` — note it is `itemId`, not the DTO's own key. */
export function useAddMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (body: {
      url: string;
      thumbnailUrl?: string;
      type: number;
    }) =>
      unwrap<unknown>(
        api.post('itemregistration/catalog/media', {
          itemId,
          url: body.url,
          thumbnailUrl: body.thumbnailUrl ?? '',
          type: body.type,
          // Appended; the drawer's reorder is what settles the real position.
          ordering: 99,
        })
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (mediaId: string) =>
      unwrap<unknown>(
        api.post(`itemregistration/catalog/removemedia/${mediaId}`, {})
      ),
    onSuccess: invalidate,
  });
}

/**
 * Ordering is written one row at a time, as the original did.
 *
 * The update takes the same binding model as the create, so the row's own
 * fields are restated rather than the DTO being echoed back.
 */
export function useReorderMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (media: RegistrationMedia[]) =>
      Promise.all(
        media.map((entry, index) =>
          unwrap<unknown>(
            api.post(`itemregistration/catalog/media/${entry.id}`, {
              itemId,
              url: entry.url,
              thumbnailUrl: entry.thumbnailUrl ?? '',
              type: entry.type,
              ordering: index,
            })
          )
        )
      ),
    onSuccess: invalidate,
  });
}

export function useUploadRegistrationMedia() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('itemregistration/media/upload/file', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
